/**
 * The report archive: every report the platform has produced, kept.
 *
 * What this replaces: a table of five fictional templates from a mock module,
 * with three buttons that did nothing. Meanwhile the real archive existed but
 * was write-only — `generated_reports` recorded that a report had been
 * produced and threw the bytes away, so a report could be listed and never
 * re-read. The bytes are now stored on generation (see api/v1/reports.py), and
 * this is the surface that hands them back.
 *
 * A report is a statement about the book at one moment. Regenerating it next
 * quarter answers a different question, which is why re-download and not
 * re-run is the operation that matters here.
 */
import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  AlertTriangle,
  CalendarClock,
  Download,
  FileSpreadsheet,
  FileText,
  Loader2,
  Search,
} from 'lucide-react'
import clsx from 'clsx'

import api from '@/services/api'
import { reportingCenterService } from '@/services/reportingCenter'

type ArchivedReport = {
  id: string
  template: string
  title: string | null
  loan_id: string | null
  status: string
  format: string | null
  size_bytes: number | null
  generated_by: string | null
  scheduled_report_id: string | null
  checksum_sha256: string | null
  downloadable: boolean
  error: string | null
  created_at: string | null
}

function humanSize(bytes: number | null): string {
  if (!bytes) return '—'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function when(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString()
}

function templateLabel(id: string): string {
  return id
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

/** Save a fetched file without navigating away from the archive. */
function saveFile(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  anchor.rel = 'noopener'
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  window.setTimeout(() => URL.revokeObjectURL(url), 30_000)
}

/** The server's reason for refusing a download. With responseType 'blob' the
 *  error body arrives as a Blob, not parsed JSON. */
async function downloadErrorMessage(error: unknown): Promise<string> {
  const data = (error as { response?: { data?: unknown } })?.response?.data
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text())
      if (typeof parsed?.detail === 'string') return parsed.detail
    } catch {
      // Not a JSON body; fall through to the generic message.
    }
  }
  return 'The stored file could not be downloaded.'
}

export default function ReportAutomation() {
  const [search, setSearch] = useState('')
  const [downloading, setDownloading] = useState<string | null>(null)
  const [downloadError, setDownloadError] = useState<{ id: string; message: string } | null>(null)

  // A plain link to the API is a browser navigation, which carries no bearer
  // token, so it is refused (401) wherever real authentication is on. The file
  // is fetched through the authenticated client and saved from memory.
  const download = async (row: ArchivedReport) => {
    setDownloading(row.id)
    setDownloadError(null)
    try {
      const fmt = (row.format || 'pdf').toLowerCase()
      const { blob, filename } = await reportingCenterService.downloadHistory(
        row.id,
        `${(row.title || row.template).replace(/[^A-Za-z0-9._-]+/g, '_')}.${fmt}`,
      )
      saveFile(blob, filename)
    } catch (error) {
      setDownloadError({ id: row.id, message: await downloadErrorMessage(error) })
    } finally {
      setDownloading(null)
    }
  }

  const history = useQuery({
    queryKey: ['reports', 'history'],
    queryFn: async () => (await api.get<ArchivedReport[]>('/reports/history', { params: { limit: 200 } })).data,
    staleTime: 15_000,
  })

  const rows = history.data ?? []

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return rows
    return rows.filter((r) =>
      [r.title, r.template, r.generated_by, r.loan_id, r.format]
        .map((v) => String(v ?? '').toLowerCase())
        .some((v) => v.includes(term)),
    )
  }, [rows, search])

  const stats = useMemo(() => {
    const kept = rows.filter((r) => r.downloadable).length
    const scheduled = rows.filter((r) => r.scheduled_report_id).length
    const bytes = rows.reduce((sum, r) => sum + (r.size_bytes ?? 0), 0)
    return { total: rows.length, kept, scheduled, bytes }
  }, [rows])

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {[
          { label: 'Reports generated', value: String(stats.total), icon: <FileText size={11} /> },
          { label: 'Archived', value: String(stats.kept), icon: <Download size={11} /> },
          { label: 'From a schedule', value: String(stats.scheduled), icon: <CalendarClock size={11} /> },
          { label: 'Stored', value: humanSize(stats.bytes), icon: <FileSpreadsheet size={11} /> },
        ].map((tile) => (
          <div key={tile.label} className="metric-cell">
            <div className="metric-cell-label flex items-center gap-1">
              {tile.icon} {tile.label}
            </div>
            <div className="metric-cell-value">{history.isPending ? '…' : tile.value}</div>
          </div>
        ))}
      </div>

      <div className="panel">
        <div className="panel-header flex-wrap gap-2">
          <span className="panel-header-title">Report archive</span>
          <label className="ml-auto flex min-w-[220px] items-center gap-1.5 rounded border border-amber-400/10 bg-black/40 px-2 py-0.5">
            <Search size={11} className="flex-shrink-0 text-stone-600" aria-hidden="true" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search title, template, who ran it…"
              aria-label="Search the report archive"
              className="min-w-0 flex-1 bg-transparent text-[11px] text-stone-300 outline-none placeholder:text-stone-600"
            />
          </label>
        </div>

        {history.isPending ? (
          <div className="flex items-center gap-2 px-4 py-6 text-[11px] text-stone-500">
            <Loader2 size={12} className="motion-safe:animate-spin" /> Reading the archive…
          </div>
        ) : history.isError ? (
          <div className="px-4 py-6 text-[11px] text-red-300">
            The archive could not be read.{' '}
            <button type="button" onClick={() => history.refetch()} className="underline hover:text-red-200">
              Retry
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="px-4 py-8 text-center text-[11px] italic text-stone-500">
            {search
              ? 'Nothing in the archive matches that.'
              : 'No reports yet. Every report generated anywhere on the platform is stored here as it is produced.'}
          </div>
        ) : (
          <table className="dense-table">
            <thead>
              <tr>
                <th>Report</th>
                <th>Template</th>
                <th>Source</th>
                <th>Format</th>
                <th>Size</th>
                <th>Generated by</th>
                <th>When</th>
                <th className="text-center">File</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => (
                <tr key={row.id}>
                  <td className="font-medium text-stone-200">{row.title || templateLabel(row.template)}</td>
                  <td>
                    <span className="rounded bg-stone-800 px-1.5 py-0.5 text-[10px] text-stone-300">
                      {templateLabel(row.template)}
                    </span>
                  </td>
                  <td className="text-[10px] text-stone-400">
                    {row.scheduled_report_id ? (
                      <span className="inline-flex items-center gap-1">
                        <CalendarClock size={10} /> Scheduled
                      </span>
                    ) : (
                      'On demand'
                    )}
                  </td>
                  <td className="font-mono text-[10px] uppercase text-stone-400">{row.format ?? '—'}</td>
                  <td className="font-mono text-[10px] text-stone-400">{humanSize(row.size_bytes)}</td>
                  <td className="text-[10px] text-stone-400">{row.generated_by ?? '—'}</td>
                  <td className="text-[10px] text-stone-500">{when(row.created_at)}</td>
                  <td className="text-center">
                    {row.downloadable ? (
                      <>
                        <button
                          type="button"
                          onClick={() => void download(row)}
                          disabled={downloading === row.id}
                          aria-label={`Download ${row.title || templateLabel(row.template)}`}
                          className="inline-flex items-center gap-1 text-[10px] text-amber-300 hover:text-amber-200 disabled:opacity-50"
                          title={row.checksum_sha256 ? `SHA-256 ${row.checksum_sha256.slice(0, 12)}…` : undefined}
                        >
                          {downloading === row.id ? (
                            <Loader2 size={10} className="motion-safe:animate-spin" />
                          ) : (
                            <Download size={10} />
                          )}{' '}
                          Download
                        </button>
                        {downloadError?.id === row.id && (
                          <div role="alert" className="mt-0.5 text-[10px] text-red-300">
                            {downloadError.message}
                          </div>
                        )}
                      </>
                    ) : (
                      <span
                        className={clsx('inline-flex items-center gap-1 text-[10px]', 'text-stone-500')}
                        title={row.error ?? 'The report was delivered but its file was not archived.'}
                      >
                        <AlertTriangle size={10} /> Not kept
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <p className="text-[10px] text-stone-600">
        Every report generated on the platform — on demand or from a schedule — is stored here as it is produced,
        with its checksum. Downloading returns the original file rather than regenerating it, so a report continues
        to say what it said on the day it was run.
      </p>
    </div>
  )
}
